import { FunctionComponent, ReactElement, useState, useEffect, useRef, Fragment } from 'react';
import { Portal, Transition } from '@headlessui/react';

import { useWindowEvent } from '../../hooks/useWindowEvent';

interface PopoverProps {
  anchorEl?: Element | null;
  onClose: () => void;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'bottom right' | 'bottom left';
  children: ReactElement;
  offsetX?: number;
  offsetY?: number;
  sameWidth?: boolean;
}

const Popover: FunctionComponent<PopoverProps> = ({
  anchorEl,
  onClose,
  placement = 'bottom',
  children,
  offsetX = 0,
  offsetY = 0,
  sameWidth = true,
}) => {
  const popperElementRef = useRef<HTMLDivElement | null>(null);
  const [styles, setStyles] = useState({});
  const [attributes, setAttributes] = useState({});
  const [isVisible, setIsVisible] = useState(false);

  const updatePosition = () => {
    if (anchorEl && popperElementRef.current) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const popperRect = popperElementRef.current.getBoundingClientRect();
      let top = 0;
      let left = 0;

      switch (placement) {
        case 'top':
          top = anchorRect.top - popperRect.height + offsetY;
          left = anchorRect.left + offsetX;
          break;
        case 'bottom':
          top = anchorRect.bottom + offsetY;
          left = anchorRect.left + offsetX;
          break;
        case 'left':
          top = anchorRect.top + offsetY;
          left = anchorRect.left - popperRect.width + offsetX;
          break;
        case 'right':
          top = anchorRect.top + offsetY;
          left = anchorRect.right + offsetX;
          break;
        case 'bottom right':
          top = anchorRect.bottom + offsetY;
          left = anchorRect.right + offsetX;
          break;
        case 'bottom left':
          top = anchorRect.bottom + offsetY;
          left = anchorRect.left + offsetX;
          break;
      }

      setStyles({
        position: 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        width: sameWidth ? `${anchorRect.width}px` : undefined,
        zIndex: 1000,
      });

      setAttributes({
        'data-placement': placement,
      });
    }
  };

  useEffect(() => {
    if (anchorEl) {
      setIsVisible(true);
      setTimeout(updatePosition, 10);
    } else {
      setIsVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl, placement, offsetX, offsetY, sameWidth]);

  useWindowEvent('mousedown', event => {
    const target = event.target as HTMLElement;

    if (anchorEl?.contains(target)) return;
    if (popperElementRef.current?.contains(target)) return;

    onClose();
  });

  useWindowEvent('scroll', () => {
    console.log('scroll');
    updatePosition();
  });

  return (
    <Portal>
      <Transition
        show={isVisible}
        as={Fragment}
        enter='ease-out duration-300'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='ease-in duration-200'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div ref={popperElementRef} {...attributes} style={styles} className='bg-white shadow-lg rounded-lg'>
          {children}
        </div>
      </Transition>
    </Portal>
  );
};

export default Popover;
